import {
  KatFileItemUpdateState,
  KatFileUploadStatus,
} from '@amzn/katal-components/es/file-upload';
import { PayloadAction } from '@reduxjs/toolkit';

export interface FileUploadStatusState {
  fileUploadStatus: FileUploadStatus;
}

export interface FileUploadStatus {
  uploadErrorCount: number;
  currentUploadInProgress: number;
  fileUploadTracker: KatFileItemUpdateState[];
}

export const fileUploadStatusDefaultState = {
  fileUploadStatus: {
    currentUploadInProgress: 0,
    fileUploadTracker: [],
    uploadErrorCount: 0,
  },
};

export interface FileAttributes {
  name: string;
  errorSummary?: string;
}

export const addFileToUploadTracker = (
  state: FileUploadStatusState,
  actions: PayloadAction<FileAttributes>,
) => {
  const file: FileAttributes = actions.payload;
  removeFileFromUploadTracker(state, actions);
  state.fileUploadStatus.fileUploadTracker.push({
    name: file.name,
    status: KatFileUploadStatus.UPLOADING,
  });
  state.fileUploadStatus.currentUploadInProgress++;
};

export const setFileToUploadComplete = (
  state: FileUploadStatusState,
  actions: PayloadAction<FileAttributes>,
) => {
  const file: FileAttributes = actions.payload;
  let index = findFileIndexInUploadTracker(
    file,
    state.fileUploadStatus.fileUploadTracker,
  );
  if (index >= 0) {
    let fileTracked = state.fileUploadStatus.fileUploadTracker[index];
    fileTracked.status = KatFileUploadStatus.COMPLETE;
    state.fileUploadStatus.currentUploadInProgress--;
  } else {
    state.fileUploadStatus.fileUploadTracker.push({
      name: file.name,
      status: KatFileUploadStatus.COMPLETE,
    });
  }
};

export const removeFileFromUploadTracker = (
  state: FileUploadStatusState,
  actions: PayloadAction<FileAttributes>,
) => {
  const file: FileAttributes = actions.payload;
  const removedFileIsErroredFile =
    state.fileUploadStatus.uploadErrorCount > 0 &&
    state.fileUploadStatus.fileUploadTracker.filter(
      (tracker) =>
        tracker.name === file.name &&
        KatFileUploadStatus.ERROR === tracker.status,
    ).length > 0;
  if (removedFileIsErroredFile) {
    state.fileUploadStatus.uploadErrorCount--;
  }
  state.fileUploadStatus.fileUploadTracker =
    state.fileUploadStatus.fileUploadTracker.filter(
      (tracker) => tracker.name !== actions.payload.name,
    );
};

export const setFileToError = (
  state: FileUploadStatusState,
  actions: PayloadAction<FileAttributes>,
) => {
  const file: FileAttributes = actions.payload;
  let index = findFileIndexInUploadTracker(
    file,
    state.fileUploadStatus.fileUploadTracker,
  );

  if (index >= 0) {
    let fileTracked = state.fileUploadStatus.fileUploadTracker[index];
    fileTracked.status = KatFileUploadStatus.ERROR;
    fileTracked.errorSummary = file.errorSummary;
    state.fileUploadStatus.currentUploadInProgress--;
  } else {
    state.fileUploadStatus.fileUploadTracker.push({
      name: file.name,
      status: KatFileUploadStatus.ERROR,
      errorSummary: file.errorSummary,
    });
  }
  state.fileUploadStatus.uploadErrorCount++;
};

const findFileIndexInUploadTracker = (
  file: FileAttributes,
  fileUploadTracker: KatFileItemUpdateState[],
): number => {
  return fileUploadTracker.findIndex((tracker) => tracker.name === file.name);
};

export const convertFileToFileAttributesAction = (
  file: File,
  errorSummary?: string,
): FileAttributes => {
  return {
    name: file.name,
    errorSummary,
  };
};
